import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import { useGetProcedures } from '@vindr/services/actions/procedure.api';
import onPaginationChange from '@vindr/utils/state/onPaginationChange';
import { Table, VindrTableProps } from '@vindr/ui';
import { Button } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import Filter from './ProcedureFilter';
import useProcedureSearchColumns from './useProcedureSearchColumns';

const ProcedureSearch = ({
  onSave,
  onCancel,
  institution_id,
}: {
  onSave: (data: ReturnType<typeof useGetProcedures>['data']['data']) => void;
  onCancel: () => void;
  institution_id: string;
}) => {
  const { t } = useTranslation('Vindoc');
  const columns = useProcedureSearchColumns();
  const initFilters = { institution_ids: institution_id ? [institution_id] : null };
  const [params, setParams] = useState(initFilters);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);
  const { data, isLoading, isFetching, refetch } = useGetProcedures({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    active: true,
    ...params,
    institution_ids: institution_id ? [institution_id] : null,
  });

  const count = data?.count;
  const procedureList = data?.data;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params) => {
    setParams(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  return (
    <div className="flex flex-col">
      <Filter
        initFilters={initFilters}
        onChange={(p) => {
          setParams(p);
        }}
        forceSearch={forceSearch}
      />
      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        columns={columns}
        loading={isLoading || isRefetch}
        dataSource={procedureList || []}
        scroll={{ y: 500 }}
        rowKey="id"
        count={count}
        wrapperClassName="h-auto"
        pagination={count < pagination.size ? null : pagination}
        onPaginationChange={(p: VindrTableProps['pagination']) => {
          setPagination((draft) => onPaginationChange(draft, p));
        }}
      />
      <div className="footer mt-3 flex justify-end">
        <Button onClick={onCancel} className="mr-1">
          {t('Cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() =>
            onSave(
              procedureList
                .filter((it) => selectedRowKeys.includes(it.id))
                .map((it) => ({ ...it, timestamp: Date.now() }))
            )
          }
        >
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default ProcedureSearch;
